import {
  Show,
  ListButton,
  EditButton,
  DeleteButton,
  RefreshButton,
  useModal,
} from "@refinedev/antd";

import { Button, Space, Tag, Row, Col } from "antd";
import { IResourceComponentsProps, useNavigation, useShow } from "@refinedev/core";
import {
  BookOutlined,
  AppstoreOutlined,
  DesktopOutlined,
} from "@ant-design/icons";

import { IMetadataAdp } from "interfaces";
import React from "react";
import dayjs from "dayjs";
import {
  ShowField,
  ShowSection,
  ShowFieldGrid,
  ShowModal,
} from "components/show";
import { formatEnrollmentDate } from "components/metadata/enrollmentDate";

export const MetadataAdpShow: React.FC<IResourceComponentsProps> = () => {
  const { result, query: { isLoading } } = useShow<IMetadataAdp>();
  const record = result;

  const { list } = useNavigation();

  const {
    modalProps: modalPropsSubscription,
    show: showSubscription,
    close: closeSubscription,
  } = useModal();

  return (
    <Show
      breadcrumb={false}
      headerProps={{
        onBack: () => window.history.back(),
        title: "Show ADP Service",
        subTitle: "active ADP service",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
        extra: (
          <Space>
            <ListButton resource="metadata-adp">ADP Service</ListButton>
            <EditButton resource="metadata-adp" recordItemId={record?.id} />
            <DeleteButton
              resource="metadata-adp"
              recordItemId={record?.id}
              onSuccess={() => list("metadata-adp")}
            />
            <RefreshButton resource="metadata-adp" recordItemId={record?.id} />
          </Space>
        ),
      }}
      isLoading={isLoading}
      footerButtons={
        <Button type="default" onClick={showSubscription}>
          Associated Subscription
        </Button>
      }
    >
      <Space direction="vertical" size={24} style={{ width: "100%" }}>
        {/* Service Overview */}
        <ShowSection
          title="ADP Service"
          subtitle="Admiralty Digital Publications configuration"
          icon={<BookOutlined />}
          variant="elevated"
        >
          <ShowFieldGrid columns={2}>
            <ShowField label="UKHO Licence" value={record?.subscription?.licence?.ukhoLicenceId} type="code" copyable />
            <ShowField label="Product Name" value={record?.productName} type="tag" tagColor="blue" />
            <ShowField label="Product Group" value={record?.productGroup} />
            <ShowField label="Licence Type" value={record?.licenceType} type="status" />
            <ShowField label="Enrollment Date" value={formatEnrollmentDate(record?.enrollmentDate)} />
            <ShowField label="Expiry Date" value={record?.expiryDate} type="date" />
          </ShowFieldGrid>
        </ShowSection>

        <Row gutter={24}>
          {/* Publication Settings */}
          <Col xs={24} lg={12}>
            <ShowSection
              title="Publication Settings"
              subtitle="Enabled publications"
              variant="default"
            >
              <ShowFieldGrid columns={1}>
                <ShowField label="ADLL Publication" value={record?.adllPublication} type="status" />
                <ShowField label="ADRS Publication" value={record?.adrsPublication} type="status" />
                <ShowField label="Total Tide Publication" value={record?.totalTidePublication} type="status" />
              </ShowFieldGrid>
            </ShowSection>
          </Col>

          {/* Subscription Details */}
          <Col xs={24} lg={12}>
            <ShowSection
              title="Subscription Details"
              subtitle="Billing and pricing"
              variant="default"
            >
              <ShowFieldGrid columns={1}>
                <ShowField label="Currency" value={record?.currency} type="tag" tagColor="purple" />
                <ShowField label="Subscription Type" value={record?.subscriptionType} />
                <ShowField label="Payment Frequency" value={record?.paymentFrequency} />
                <ShowField label="Discount Percentage" value={record?.discountPercentage} type="percentage" />
                <ShowField label="Annual Flat Fee Per Vessel" value={record?.annualFlatFeePerVessel} type="currency" currency={record?.currency} />
              </ShowFieldGrid>
            </ShowSection>
          </Col>
        </Row>

        {/* Credentials */}
        <ShowSection
          title="Credentials"
          subtitle="TID and startup key"
          variant="subtle"
        >
          <ShowFieldGrid columns={2}>
            <ShowField label="TID" value={record?.tid} type="code" copyable />
            <ShowField label="Startup Key" value={record?.startupKey} type="code" copyable />
          </ShowFieldGrid>
        </ShowSection>

        {/* Machine Configuration */}
        <ShowSection
          title="Machine Configuration"
          subtitle="Master and backup machine details"
          icon={<DesktopOutlined />}
          variant="subtle"
        >
          <Row gutter={24}>
            <Col xs={24} lg={12}>
              <ShowSection title="Master Machine" variant="outlined" padding="compact">
                <ShowFieldGrid columns={1}>
                  <ShowField label="Machine Name" value={record?.masterMachineName} />
                  <ShowField label="Machine ID" value={record?.masterMachineId} type="code" copyable />
                  <ShowField label="Machine Key" value={record?.masterMachineKey} type="code" copyable />
                  <ShowField label="Machine Version" value={record?.masterMachineVersion} />
                </ShowFieldGrid>
              </ShowSection>
            </Col>
            <Col xs={24} lg={12}>
              <ShowSection title="Backup Machine" variant="outlined" padding="compact">
                <ShowFieldGrid columns={1}>
                  <ShowField label="Machine Name" value={record?.backupMachineName} />
                  <ShowField label="Machine ID" value={record?.backupMachineId} type="code" copyable />
                  <ShowField label="Machine Key" value={record?.backupMachineKey} type="code" copyable />
                  <ShowField label="Machine Version" value={record?.backupMachineVersion} />
                </ShowFieldGrid>
              </ShowSection>
            </Col>
          </Row>
        </ShowSection>
      </Space>

      {/* Subscription Modal */}
      <ShowModal
        title="Associated Subscription"
        subtitle={record?.subscription?.subscriptionName}
        icon={<AppstoreOutlined />}
        open={modalPropsSubscription.open ?? false}
        onClose={closeSubscription}
        empty={!record?.subscription}
        emptyText="No subscription associated"
        width={700}
      >
        <ShowFieldGrid columns={2}>
          <ShowField label="UKHO Licence" value={record?.subscription?.licence?.ukhoLicenceId} type="code" copyable />
          <ShowField label="Subscription Name" value={record?.subscription?.subscriptionName} />
          <ShowField label="Service Type" value={record?.subscription?.serviceType} type="tag" tagColor="purple" />
          <ShowField
            label="Enrollment Date"
            value={record?.subscription?.enrollmentDate ? dayjs(record.subscription.enrollmentDate).format("DD-MMM-YYYY") : undefined}
          />
          <ShowField label="Licence Type" value={record?.subscription?.licenceType} type="status" />
          <ShowField label="Activation Status" value={record?.subscription?.activationStatus} type="status" />
          <ShowField label="Hold Status" value={record?.subscription?.holdStatus} type="status" />
          <ShowField label="Order Value Threshold" value={record?.subscription?.orderValueThreshold} type="currency" />
          <ShowField label="Automated Ordering" value={record?.subscription?.automatedOrdering} type="status" />
          <ShowField label="Automated Permit Issuance" value={record?.subscription?.automatedPermitIssuance} type="status" />
        </ShowFieldGrid>
      </ShowModal>
    </Show>
  );
};
